import { useMemo } from 'react';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry } from '@/pages/Schedule/types';
import { createRecurrenceKey } from '../utils';

export const useDraftEntry = () => {
  const { draftEntry, draftMode, draftComposer } = useDraftEntryStore(
    (state) => ({
      draftEntry: state.draftEntry,
      draftMode: state.draftMode,
      draftComposer: state.draftComposer,
    })
  );

  // Capture a snapshot of the draft entry when the modal is opened
  const draftEntrySnapshot = useMemo<DecoratedEntry | undefined>(
    () => draftEntry,
    [draftMode] // eslint-disable-line
  );

  // Serialize the draft entry's recurrence object into a string that can be used
  // to trigger a recalculation of entries when something about the recurrence
  // changes. We don't want to trigger a recalculation using the recurrence
  // object itself because it's not referentially stable, even if the values
  // themselves don't change.
  const recurrenceKey = useMemo<string | undefined>(
    () => createRecurrenceKey(draftEntry),
    [draftEntry]
  );

  // When something in the recurrence object changes, create a new draft entry
  // object from the one that the modal originally opened with (to exclude any
  // changes the user may have made), then overlay recurrence changes on top of
  // it. This new object drives the display of the entry on the schedule while
  // it's being created/edited.
  const draftEntryPlaceholder = useMemo<DecoratedEntry | undefined>(() => {
    if (draftEntry && draftEntrySnapshot) {
      return {
        ...draftEntrySnapshot,
        recurrences: draftEntry.recurrences,
      };
    }
    // `draftEntry` is intentionally omitted because we want to capture it's
    // value when one of the listed dependencies change but not trigger a
    // update when the draft itself changes.
    // eslint-disable-next-line
  }, [draftEntrySnapshot, recurrenceKey]);

  return {
    draftEntry: draftEntryPlaceholder,
    draftMode,
    draftComposer,
  };
};
